import {
  useCallback,
  useEffect,
  useRef,
} from 'react'

interface UseLongPressOptions {
  delay?: number
  moveThreshold?: number
  onLongPress: () => void
  onClick?: () => void
}

export const useLongPressFullscreenImage = ({
  delay = 500,
  moveThreshold = 20,
  onLongPress,
  onClick,
}: UseLongPressOptions) => {
  const timerRef = useRef<number | null>(null)

  const startPointRef = useRef<{
    x: number
    y: number
  } | null>(null)

  const activePointersRef = useRef<Set<number>>(
    new Set()
  )

  const pressingRef = useRef(false)
  const longPressTriggeredRef = useRef(false)
  const gestureCancelledRef = useRef(false)

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const cancelPress = useCallback(() => {
    clearTimer()

    pressingRef.current = false
    startPointRef.current = null
    gestureCancelledRef.current = true
  }, [clearTimer])

  const startPress = useCallback(
    (
      x: number,
      y: number
    ) => {
      clearTimer()

      pressingRef.current = true
      longPressTriggeredRef.current = false
      gestureCancelledRef.current = false

      startPointRef.current = {
        x,
        y,
      }

      timerRef.current =
        window.setTimeout(() => {
          if (
            !pressingRef.current ||
            gestureCancelledRef.current ||
            activePointersRef.current.size !== 1
          ) {
            return
          }

          longPressTriggeredRef.current = true
          pressingRef.current = false

          onLongPress()
        }, delay)
    },
    [
      clearTimer,
      delay,
      onLongPress,
    ]
  )

  const handlePointerDown = useCallback(
    (
      e: React.PointerEvent<HTMLElement>
    ) => {
      if (
        e.pointerType === 'mouse' &&
        e.button !== 0
      ) {
        return
      }

      activePointersRef.current.add(
        e.pointerId
      )

      /*
       * Second finger = pinch/multi-touch.
       *
       * Cancel our pending long press and
       * leave the gesture entirely to Swiper.
       */
      if (
        activePointersRef.current.size > 1
      ) {
        cancelPress()
        return
      }

      startPress(
        e.clientX,
        e.clientY
      )
    },
    [
      cancelPress,
      startPress,
    ]
  )

  const handlePointerMove = useCallback(
    (
      e: React.PointerEvent<HTMLElement>
    ) => {
      /*
       * Never interfere with multi-touch.
       */
      if (
        activePointersRef.current.size > 1
      ) {
        cancelPress()
        return
      }

      if (
        !pressingRef.current ||
        !startPointRef.current
      ) {
        return
      }

      const dx = Math.abs(
        e.clientX -
          startPointRef.current.x
      )

      const dy = Math.abs(
        e.clientY -
          startPointRef.current.y
      )

      /*
       * A swipe/pan should not become
       * a long press.
       */
      if (
        dx > moveThreshold ||
        dy > moveThreshold
      ) {
        cancelPress()
      }
    },
    [
      cancelPress,
      moveThreshold,
    ]
  )

  const handlePointerUp = useCallback(
    (
      e: React.PointerEvent<HTMLElement>
    ) => {
      activePointersRef.current.delete(
        e.pointerId
      )

      clearTimer()

      /*
       * If this gesture became a pinch,
       * swipe or pan, do nothing.
       */
      if (
        gestureCancelledRef.current
      ) {
        if (
          activePointersRef.current.size ===
          0
        ) {
          gestureCancelledRef.current =
            false

          pressingRef.current = false
          startPointRef.current = null
          longPressTriggeredRef.current =
            false
        }

        return
      }

      /*
       * Long press already fired.
       * Do not also fire a normal click.
       */
      if (
        longPressTriggeredRef.current
      ) {
        longPressTriggeredRef.current =
          false

        pressingRef.current = false
        startPointRef.current = null

        return
      }

      /*
       * Normal short single-finger tap.
       */
      if (
        pressingRef.current &&
        activePointersRef.current.size === 0
      ) {
        onClick?.()
      }

      pressingRef.current = false
      startPointRef.current = null
    },
    [
      clearTimer,
      onClick,
    ]
  )

  const handlePointerCancel =
    useCallback(
      (
        e: React.PointerEvent<HTMLElement>
      ) => {
        activePointersRef.current.delete(
          e.pointerId
        )

        cancelPress()

        if (
          activePointersRef.current.size ===
          0
        ) {
          gestureCancelledRef.current =
            false
        }
      },
      [cancelPress]
    )

  const handlePointerLeave =
    useCallback(
      (
        e: React.PointerEvent<HTMLElement>
      ) => {
        /*
         * Don't cancel simply because a
         * touch pointer leaves the original
         * element while Swiper is handling it.
         *
         * Mouse leaving should cancel hold.
         */
        if (
          e.pointerType === 'mouse'
        ) {
          activePointersRef.current.delete(
            e.pointerId
          )

          cancelPress()
        }
      },
      [cancelPress]
    )

  useEffect(() => {
    const handleScroll = () => {
      if (pressingRef.current) {
        cancelPress()
      }
    }

    window.addEventListener(
      'scroll',
      handleScroll,
      true
    )

    return () => {
      window.removeEventListener(
        'scroll',
        handleScroll,
        true
      )

      clearTimer()
    }
  }, [
    cancelPress,
    clearTimer,
  ])

  return {
    onPointerDown:
      handlePointerDown,

    onPointerMove:
      handlePointerMove,

    onPointerUp:
      handlePointerUp,

    onPointerCancel:
      handlePointerCancel,

    onPointerLeave:
      handlePointerLeave,

    onContextMenu: (
      e: React.MouseEvent
    ) => {
      e.preventDefault()
    },

    onDragStart: (
      e: React.DragEvent
    ) => {
      e.preventDefault()
    },
  }
}
